import ExcelJS from 'exceljs'
import { supabase } from '../supabaseClient'
import type { PortalHotel } from '../types'
import { ALLOCATIONS_HEADERS, COMPANIES_HEADERS, GUESTS_HEADERS, autoSizeColumns, downloadBlob, styleHeaderRow, workbookToBlob } from './shared'

/**
 * Downloads an Excel workbook pre-populated with one company's current
 * allocation and rooms (section 44). The Guests sheet already has a row for
 * every occupied slot with its internal Guest ID filled in, so editing
 * names in Excel and re-importing updates those exact guests rather than
 * creating duplicates (section 43).
 */
export async function buildCompanyTemplate(opts: {
  companyId: string
  eventName: string
  companyCode: string
  companyName: string
  contactPerson: string | null
  contactMobile: string | null
  contactEmail: string | null
}): Promise<void> {
  const { data: hotels, error } = await supabase.rpc('admin_company_room_tree', { p_company_id: opts.companyId })
  if (error) throw error

  const wb = new ExcelJS.Workbook()

  const companies = wb.addWorksheet('Companies')
  companies.addRow(COMPANIES_HEADERS)
  styleHeaderRow(companies.getRow(1))
  companies.addRow([opts.companyCode, opts.companyName, opts.contactPerson || '', opts.contactMobile || '', opts.contactEmail || '', '', '', ''])
  autoSizeColumns(companies)

  const allocations = wb.addWorksheet('Allocations')
  allocations.addRow(ALLOCATIONS_HEADERS)
  styleHeaderRow(allocations.getRow(1))

  const guests = wb.addWorksheet('Guests')
  guests.addRow(GUESTS_HEADERS)
  styleHeaderRow(guests.getRow(1))

  for (const hotel of (hotels as PortalHotel[]) || []) {
    for (const rt of hotel.room_types) {
      allocations.addRow([
        opts.companyCode,
        hotel.name,
        rt.name,
        rt.rooms.length,
        rt.rooms[0]?.capacity || 1,
        rt.rooms[0]?.check_in || '',
        rt.rooms[0]?.check_out || '',
        '',
      ])
      for (const room of rt.rooms) {
        for (const guest of room.guests) {
          guests.addRow([
            opts.companyCode,
            hotel.name,
            rt.name,
            room.sequence_number,
            guest.slot_number,
            guest.full_name || '',
            guest.mobile || '',
            guest.email || '',
            guest.check_in || room.check_in || '',
            guest.check_out || room.check_out || '',
            guest.notes || '',
            guest.id,
          ])
        }
      }
    }
  }

  autoSizeColumns(allocations)
  autoSizeColumns(guests)

  const blob = await workbookToBlob(wb)
  downloadBlob(blob, `${opts.companyCode}_rooming_list.xlsx`)
}
